import axios from "axios";

/**
 * Fetches category 2 program data from the Jackrabbit API
 *
 * This function is similar to fetchPrograms but specifically designed for category 2 programs.
 * It makes a POST request to the Ace Gymnastics API endpoint to retrieve program information
 * from the Jackrabbit class management system. Unlike fetchPrograms, this function returns
 * the raw data instead of dispatching to Redux store, allowing for more flexible usage
 * in components that need direct access to the program data.
 *
 * Category 2 programs typically refer to a specific classification of programs within
 * the Jackrabbit system, possibly representing different program types or levels.
 *
 * @param {Object} filters - Optional filters to apply to the program search
 * @param {string} [filters.ProgramId] - Filter by specific program ID
 * @param {string} [filters.LocationId] - Filter by specific location ID
 * @param {string} [filters.ClassId] - Filter by specific class ID
 * @param {string} [filters.StartDate] - Filter by start date
 * @param {string} [filters.EndDate] - Filter by end date
 * @param {boolean} [filters.ShowClosed=true] - Whether to include closed classes (defaults to true)
 * @param {string} [filters.Category] - Filter by program category (likely set to "2" for this function)
 *
 * @returns {Promise<Array>} A promise that resolves with an array of program/class data
 * @returns {Array} returns - Array of category 2 program data from Jackrabbit
 *
 * @throws {Error} Logs error to console if the API request fails
 *
 * @example
 * ```javascript
 * // Fetch all category 2 programs
 * const programs = await fetchCat2Programs();
 * console.log(programs); // Array of program data
 *
 * // Fetch category 2 programs with filters
 * const filteredPrograms = await fetchCat2Programs({
 *   ProgramId: "123",
 *   LocationId: "456",
 *   StartDate: "2024-01-01"
 * });
 * ```
 *
 * @example
 * ```javascript
 * // Use in a React component
 * const [cat2Programs, setCat2Programs] = useState([]);
 *
 * useEffect(() => {
 *   fetchCat2Programs().then(data => {
 *     setCat2Programs(data);
 *   });
 * }, []);
 * ```
 *
 * @see fetchPrograms - Similar function that dispatches to Redux store
 */
export const fetchCat2Programs = (filters) => {
  return axios({
    method: "POST",
    url: "https://ace-gymnastics-api.herokuapp.com/programs",
    // url: "http://localhost:9000/programs",
    data: {
      OrgId: process.env.REACT_APP_ORGID,
      ShowClosed: true,
      ...filters,
    },
  })
    .then((result) => {
      return result.data.rows;
    })
    .catch((error) => {
      console.log("catch", error);
    });
};
